import _ from "lodash";
import {
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_ERROR_TITLE,
  defaultBlogUrl,
  notificationDuration,
} from "../../data/constants";
import { createBlogAsync } from "../../helpers/async/createBlogAsync";
import { AuthState } from "../auth/types";
import {
  saveSingleBlogInState,
  setSingleBlogLoadStatus,
} from "../blogs/actions";
import { BlogsState } from "../blogs/types";
import { getCurrentStoreData } from "../websites/thunks";
import { openNotification } from "../../helpers/openNotification";
import {
  cloneBlogPostWaitingForServer,
  createBlogPostWaitingForServer,
  deleteBlogPostWaitingForServer,
  publishPostToggleWaitingForServer,
  removeSingleBlogPostFromStore,
  saveBlogsPostsInState,
  setInitialPostsStatus,
  setSinglePostStatus,
} from "./actions";
import { createPostAsync } from "../../helpers/async/createPostAsync";
import { format } from "fecha";
import { generateUniqueUrl } from "../../helpers/generateUniqueUrl";
import { BlogPostsState } from "./types";
import { LoadStatus } from "../../enums/enums";
import { formatToURLPath } from "../../helpers/strings/formatToURLPath";
import trimStringTo from "../../helpers/strings/trimStringTo";
import { updatePostAsync } from "../../helpers/async/updatePostAsync";
import { getActiveDomain } from "../../helpers/websites/getActiveDomain";
import addNoCacheParamToURL from "../../helpers/editor/addNoCacheParamToURL";
import { clonePostAsync } from "../../helpers/async/clonePostAsync";
import { deletePostAsync } from "../../helpers/async/deletePostAsync";

export const createAndSavePost = () => {
  return (dispatch, getState) => {
    const auth: AuthState = getState().auth;
    const blogs: BlogsState = getState().blogs;
    const blogPosts: BlogPostsState = getState().blogPosts;

    const { currentWebsite } = dispatch(getCurrentStoreData());
    let currentBlog = blogs.items.find((blog) => {
      return blog.website === _.get(currentWebsite, "id");
    });

    const createBlog = async () => {
      return await createBlogAsync({
        accessToken: auth.accessToken,
        subdomain: currentWebsite.subdomain,
        newBlogUrl: defaultBlogUrl,
        websiteId: currentWebsite.id,
      })
        .then(({ data }) => {
          dispatch(
            setSingleBlogLoadStatus({
              blogId: data.id,
              status: LoadStatus.LOADED,
            })
          );
          dispatch(
            setInitialPostsStatus({
              blogId: data.id,
              status: LoadStatus.LOADED,
            })
          );
          dispatch(saveSingleBlogInState([data]));
          currentBlog = data;
          return data;
        })
        .catch((error) => {
          console.error(error.response);
          openNotification(
            DEFAULT_ERROR_TITLE,
            DEFAULT_ERROR_MESSAGE,
            "OK",
            "error",
            notificationDuration.medium
          );
        });
    };

    return new Promise(async (resolve, reject) => {
      dispatch(createBlogPostWaitingForServer(true));

      if (!currentBlog) {
        const response = await createBlog();
        if (!response) {
          dispatch(createBlogPostWaitingForServer(false));
          return;
        }
      }

      const defaultEditorData = {
        time: new Date().getTime(),
        blocks: [],
        version: "2.11.10",
      };

      const currentBlogPosts = blogPosts.items.filter(
        (post) => post.blog === currentBlog.id
      );
      const newPostUrl = generateUniqueUrl("untitled-post", currentBlogPosts);

      createPostAsync({
        accessToken: auth.accessToken,
        subdomain: currentWebsite.subdomain,
        newPostUrl,
        blogId: currentBlog.id,
        body_json: defaultEditorData,
        published_on: format(new Date(), "YYYY-MM-DD HH:mm:ss"),
      })
        .then(({ data }) => {
          dispatch(
            setSinglePostStatus({
              postId: data.id,
              status: LoadStatus.LOADED,
            })
          );
          dispatch(saveBlogsPostsInState([data]));
          resolve({ currentBlog, postData: data });
        })
        .catch((error) => {
          console.error(error.response);
          const warningMessage = _.get(error, "response.data.limit");
          if (warningMessage) {
            openNotification(
              "Error",
              warningMessage,
              "OK",
              "warning",
              notificationDuration.medium
            );
            return;
          }
          openNotification(
            DEFAULT_ERROR_TITLE,
            DEFAULT_ERROR_MESSAGE,
            "OK",
            "error",
            notificationDuration.medium
          );
          reject(error);
        })
        .finally(() => {
          dispatch(createBlogPostWaitingForServer(false));
        });
    });
  };
};

export const getSinglePostLoadStatus = (postId) => {
  return (dispatch, getState) => {
    if (!postId) {
      return LoadStatus.NO_DATA;
    }
    const blogPosts = getState().blogPosts;
    const currentStatus = blogPosts.singlePostStatusArray.find((item) => {
      return item.postId === postId;
    });
    return _.get(currentStatus, "status", LoadStatus.NOT_LOADED);
  };
};

export const getInitialPostsStatus = (blog) => {
  return (dispatch, getState) => {
    const blogs = getState().blogs;
    if (!blog && blogs.dataFetched) {
      return LoadStatus.LOADED;
    }
    if (!blog) {
      return LoadStatus.NO_DATA;
    }
    const blogPosts = getState().blogPosts;
    const currentStatus = blogPosts.initialPostsStatusArray.find((item) => {
      return item.blogId === blog.id;
    });
    return _.get(currentStatus, "status", LoadStatus.NOT_LOADED);
  };
};

export const publishPostFromEditor = (
  callBack: any,
  saveBlogPostDataInStore: any
) => {
  return (dispatch, getState) => {
    const auth: AuthState = getState().auth;
    const blogPosts: BlogPostsState = getState().blogPosts;
    const { currentWebsite, currentBlogPost, currentBlog } = dispatch(
      getCurrentStoreData()
    );
    if (!currentBlogPost || !currentWebsite) {
      return;
    }
    dispatch(publishPostToggleWaitingForServer(true));
    let urlToServer = currentBlogPost.url;
    if (
      typeof currentBlogPost.url === "string" &&
      currentBlogPost.url.startsWith("untitled-post") &&
      currentBlogPost.title
    ) {
      urlToServer = formatToURLPath(
        trimStringTo(currentBlogPost.title, 150).replace(/\//g, "")
      );
    }
    const postData = {
      status: "public",
    };
    if (urlToServer !== currentBlogPost.url) {
      postData["url"] = urlToServer;
    }
    updatePostAsync({
      accessToken: auth.accessToken,
      subdomain: currentWebsite.subdomain,
      postUrl: currentBlogPost.url,
      data: postData as any,
    })
      .then(() => {
        saveBlogPostDataInStore(
          {
            data: {
              ...currentBlogPost,
              ...postData,
            },
          },
          currentBlog,
          blogPosts.items,
          currentBlogPost.url
        );
        if (callBack) {
          callBack(urlToServer);
        }
      })
      .catch((error) => {
        console.log("error: ", error);
        console.error(error.response);
        openNotification(
          DEFAULT_ERROR_TITLE,
          DEFAULT_ERROR_MESSAGE,
          "OK",
          "error",
          notificationDuration.medium
        );
      })
      .finally(() => {
        dispatch(publishPostToggleWaitingForServer(false));
      });
  };
};

export const previewPost = (postId: number) => {
  return (dispatch, getState) => {
    let protocol = "https://";
    if (process.env.REACT_APP_API_URL === "http://app.unicorn.com:8000") {
      protocol = "http://";
    }
    const post = getState().blogPosts.items.find((item) => item.id === postId);
    if (!post) return;
    const blog = getState().blogs.items.find((item) => item.id === post.blog);
    if (!blog) return;
    const website = getState().websites.items.find(
      (item) => item.id === blog.website
    );
    if (!website) return;

    let openPostUrl = `${protocol}${getActiveDomain(website)}`;
    openPostUrl += !blog.url ? "/" : `/${blog.url}/`;
    openPostUrl += post.url;
    window.open(addNoCacheParamToURL(openPostUrl), `postPreview-${postId}`);
  };
};

export const duplicatePost = (blogPost) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      if (!blogPost) return;

      const { auth, websites, blogs } = getState();
      const currentPostBlog = blogs.items.find(
        (blog) => blog.id === blogPost.blog
      );
      const currentPostWebsite = websites.items.find((website) => {
        return website.id === currentPostBlog.website;
      });

      dispatch(cloneBlogPostWaitingForServer(true));
      clonePostAsync({
        accessToken: auth.accessToken,
        postUrl: blogPost.url,
        subdomain: currentPostWebsite.subdomain,
      })
        .then(({ data }) => {
          dispatch(
            setSinglePostStatus({
              postId: data.id,
              status: LoadStatus.LOADED,
            })
          );
          dispatch(saveBlogsPostsInState([data]));
          resolve(data);
        })
        .catch((error) => {
          console.error(error.response);
          const warningMessage = _.get(error, "response.data.limit");
          if (warningMessage) {
            openNotification(
              "Error",
              warningMessage,
              "OK",
              "warning",
              notificationDuration.medium
            );
            return;
          }
          openNotification(
            DEFAULT_ERROR_TITLE,
            DEFAULT_ERROR_MESSAGE,
            "OK",
            "error",
            notificationDuration.medium
          );
          reject(error);
        })
        .finally(() => {
          dispatch(cloneBlogPostWaitingForServer(false));
        });
    });
  };
};

export const deletePost = (blogPost) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      if (!blogPost) return;

      const { auth, websites, blogs } = getState();

      const currentPostBlog = blogs.items.find(
        (blog) => blog.id === blogPost.blog
      );
      const currentPostWebsite = websites.items.find(
        (website) => website.id === currentPostBlog.website
      );

      dispatch(deleteBlogPostWaitingForServer(true));
      deletePostAsync({
        accessToken: auth.accessToken,
        subdomain: currentPostWebsite.subdomain,
        postUrl: blogPost.url,
      })
        .then((response) => {
          dispatch(
            setSinglePostStatus({
              postId: blogPost.id,
              status: LoadStatus.DELETED,
            })
          );
          dispatch(
            removeSingleBlogPostFromStore({
              blogId: currentPostBlog.id,
              postUrl: blogPost.url,
            })
          );
          resolve(response);
        })
        .catch((error) => {
          console.error(error.response);
          openNotification(
            DEFAULT_ERROR_TITLE,
            DEFAULT_ERROR_MESSAGE,
            "OK",
            "error",
            notificationDuration.medium
          );
          reject(error);
        })
        .finally(() => {
          dispatch(deleteBlogPostWaitingForServer(false));
        });
    });
  };
};
